import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { 
  ChevronLeft, 
  Download, 
  Mail, 
  Users, 
  Calendar,
  Search,
  Send,
  Clock,
  UserPlus,
  Trash2,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminParticipants() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [filteredWaitlist, setFilteredWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [messageDialog, setMessageDialog] = useState(false);
  const [messageData, setMessageData] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('participants');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, [eventId]);

  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      setFilteredParticipants(participants.filter(p =>
        p.first_name.toLowerCase().includes(query) ||
        p.last_name.toLowerCase().includes(query) ||
        p.email.toLowerCase().includes(query)
      ));
      setFilteredWaitlist(waitlist.filter(w =>
        w.first_name.toLowerCase().includes(query) ||
        w.last_name.toLowerCase().includes(query) ||
        w.email.toLowerCase().includes(query)
      ));
    } else {
      setFilteredParticipants(participants);
      setFilteredWaitlist(waitlist);
    }
  }, [participants, waitlist, searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch event, participants, and waitlist in parallel
      const [eventRes, participantsRes, waitlistRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/events/${eventId}`),
        fetch(`${BACKEND_URL}/api/admin/events/${eventId}/participants`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/events/${eventId}/waitlist`, { credentials: 'include' })
      ]);

      if (eventRes.ok) {
        const eventData = await eventRes.json();
        setEvent(eventData);
      }

      if (participantsRes.ok) {
        const participantsData = await participantsRes.json();
        setParticipants(participantsData);
        setFilteredParticipants(participantsData);
      }

      if (waitlistRes.ok) {
        const waitlistData = await waitlistRes.json();
        setWaitlist(waitlistData);
        setFilteredWaitlist(waitlistData);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/events/${eventId}/export`,
        { credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Erreur lors de l\'export');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `participants_${eventId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSendMessage = async () => {
    if (!messageData.subject || !messageData.message) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      setSending(true);
      const response = await fetch(
        `${BACKEND_URL}/api/admin/events/${eventId}/message`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(messageData)
        }
      );

      if (!response.ok) throw new Error('Erreur lors de l\'envoi');

      toast.success(`Message envoyé à ${participants.length} participant(s)`);
      setMessageDialog(false);
      setMessageData({ subject: '', message: '' });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  const handlePromoteFromWaitlist = async (waitlistId) => {
    if (!confirm('Êtes-vous sûr de vouloir inscrire cette personne ?')) return;
    
    try {
      setActionLoading(waitlistId);
      const response = await fetch(
        `${BACKEND_URL}/api/admin/events/${eventId}/waitlist/${waitlistId}/promote`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (!response.ok) throw new Error('Erreur lors de l\'inscription');

      toast.success('Inscrit avec succès et notifié par email');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveFromWaitlist = async (waitlistId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette personne de la liste d\'attente ?')) return;
    
    try {
      setActionLoading(waitlistId);
      const response = await fetch(
        `${BACKEND_URL}/api/admin/events/${eventId}/waitlist/${waitlistId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (!response.ok) throw new Error('Erreur lors de la suppression');

      toast.success('Supprimé de la liste d\'attente');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteRegistration = async (registrationId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette inscription ? Si une personne est en liste d\'attente, elle sera automatiquement inscrite.')) return;
    
    try {
      setActionLoading(registrationId);
      const response = await fetch(
        `${BACKEND_URL}/api/admin/registrations/${registrationId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (!response.ok) throw new Error('Erreur lors de la suppression');

      const result = await response.json();
      toast.success(result.message);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const totalParticipants = participants.reduce((sum, p) => sum + p.num_participants, 0);

  return (
    <AdminLayout title="Participants">
      <div className="space-y-6">
        {/* Back link */}
        <Link 
          to={`/admin/evenements/${eventId}`}
          className="inline-flex items-center text-[#645E55] hover:text-[#2D2A26] transition-colors"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Retour à l'événement
        </Link>

        {/* Event info card */}
        {event && (
          <Card className="border-2 border-[#E5E0D8] rounded-2xl bg-[#FFF8E1]">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h2 className="font-heading text-xl font-bold text-[#2D2A26] mb-2">
                    {event.name}
                  </h2>
                  <div className="flex flex-wrap items-center gap-4 text-[#645E55]">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{format(new Date(event.date), "d MMMM yyyy 'à' HH'h'mm", { locale: fr })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <span>{totalParticipants} / {event.max_participants} inscrits</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={handleExportCSV}
                    variant="outline"
                    className="border-2 border-[#E5E0D8] bg-white rounded-xl"
                    disabled={participants.length === 0}
                    data-testid="export-csv-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                  <Button
                    onClick={() => setMessageDialog(true)}
                    className="btn-primary"
                    disabled={participants.length === 0}
                    data-testid="send-message-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Envoyer un message
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#645E55]" />
          <Input
            type="text"
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11 border-2 border-[#E5E0D8] rounded-xl"
            data-testid="search-participants"
          />
        </div>

        {/* Tabs for Participants and Waitlist */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#F5F3EF] p-1 rounded-xl mb-4">
            <TabsTrigger 
              value="participants"
              className="data-[state=active]:bg-white data-[state=active]:shadow rounded-lg px-4 py-2"
              data-testid="tab-participants"
            >
              <Users className="w-4 h-4 mr-2" />
              Inscrits ({participants.length})
            </TabsTrigger>
            <TabsTrigger 
              value="waitlist"
              className="data-[state=active]:bg-white data-[state=active]:shadow rounded-lg px-4 py-2"
              data-testid="tab-waitlist"
            >
              <Clock className="w-4 h-4 mr-2" />
              Liste d'attente ({waitlist.length})
            </TabsTrigger>
          </TabsList>

          {/* Participants Tab */}
          <TabsContent value="participants">
            <div className="bg-white rounded-2xl border-2 border-[#E5E0D8] overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="w-8 h-8 border-4 border-[#db3b17] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-[#645E55]">Chargement...</p>
                </div>
              ) : filteredParticipants.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-5xl mb-4">👥</div>
                  <h3 className="font-heading font-bold text-xl text-[#2D2A26] mb-2">
                    {searchQuery ? 'Aucun participant trouvé' : 'Aucun inscrit pour le moment'}
                  </h3>
                  <p className="text-[#645E55]">
                    {searchQuery ? 'Modifiez votre recherche' : 'Les inscriptions apparaîtront ici'}
                  </p>
                </div>
              ) : (
                <Table className="admin-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Téléphone</TableHead>
                      <TableHead>Participants</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Inscription</TableHead>
                      <TableHead className="w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredParticipants.map((participant) => (
                      <TableRow key={participant.registration_id} data-testid={`participant-row-${participant.registration_id}`}>
                        <TableCell>
                          <span className="font-semibold text-[#2D2A26]">
                            {participant.first_name} {participant.last_name}
                          </span>
                        </TableCell>
                        <TableCell>
                          <a href={`mailto:${participant.email}`} className="text-[#48b4ff] hover:underline">
                            {participant.email}
                          </a>
                        </TableCell>
                        <TableCell>
                          {participant.phone ? (
                            <a href={`tel:${participant.phone}`} className="text-[#645E55] hover:text-[#2D2A26]">
                              {participant.phone}
                            </a>
                          ) : (
                            <span className="text-[#E5E0D8]">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{participant.num_participants}</span>
                        </TableCell>
                        <TableCell>
                          <code className="bg-[#FFF8E1] px-2 py-1 rounded text-sm font-mono">
                            {participant.confirmation_code}
                          </code>
                        </TableCell>
                        <TableCell className="text-[#645E55]">
                          {format(new Date(participant.created_at), "d MMM yyyy", { locale: fr })}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteRegistration(participant.registration_id)}
                            disabled={actionLoading === participant.registration_id}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Annuler l'inscription"
                            data-testid={`delete-registration-${participant.registration_id}`}
                          >
                            {actionLoading === participant.registration_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Summary */}
            {!loading && participants.length > 0 && (
              <div className="flex justify-between items-center text-[#645E55] text-sm mt-4">
                <span>{filteredParticipants.length} inscription(s) affichée(s)</span>
                <span>Total : {totalParticipants} participant(s)</span>
              </div>
            )}
          </TabsContent>

          {/* Waitlist Tab */}
          <TabsContent value="waitlist">
            <div className="bg-white rounded-2xl border-2 border-[#E5E0D8] overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="w-8 h-8 border-4 border-[#db3b17] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-[#645E55]">Chargement...</p>
                </div>
              ) : filteredWaitlist.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-5xl mb-4">⏳</div>
                  <h3 className="font-heading font-bold text-xl text-[#2D2A26] mb-2">
                    {searchQuery ? 'Aucune personne trouvée' : 'Personne en liste d\'attente'}
                  </h3>
                  <p className="text-[#645E55]">
                    {searchQuery ? 'Modifiez votre recherche' : 'Les inscriptions en attente apparaîtront ici'}
                  </p>
                </div>
              ) : (
                <Table className="admin-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[60px]">Position</TableHead>
                      <TableHead>Nom</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Téléphone</TableHead>
                      <TableHead>Participants</TableHead>
                      <TableHead>Inscription</TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredWaitlist.map((entry) => (
                      <TableRow key={entry.waitlist_id} data-testid={`waitlist-row-${entry.waitlist_id}`}>
                        <TableCell>
                          <Badge variant="outline" className="font-bold">
                            #{entry.position}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="font-semibold text-[#2D2A26]">
                            {entry.first_name} {entry.last_name}
                          </span>
                        </TableCell>
                        <TableCell>
                          <a href={`mailto:${entry.email}`} className="text-[#48b4ff] hover:underline">
                            {entry.email}
                          </a>
                        </TableCell>
                        <TableCell>
                          {entry.phone ? (
                            <a href={`tel:${entry.phone}`} className="text-[#645E55] hover:text-[#2D2A26]">
                              {entry.phone}
                            </a>
                          ) : (
                            <span className="text-[#E5E0D8]">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{entry.num_participants}</span>
                        </TableCell>
                        <TableCell className="text-[#645E55]">
                          {format(new Date(entry.created_at), "d MMM yyyy", { locale: fr })}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePromoteFromWaitlist(entry.waitlist_id)}
                              disabled={actionLoading === entry.waitlist_id}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Inscrire cette personne"
                              data-testid={`promote-waitlist-${entry.waitlist_id}`}
                            >
                              {actionLoading === entry.waitlist_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <UserPlus className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveFromWaitlist(entry.waitlist_id)}
                              disabled={actionLoading === entry.waitlist_id}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Supprimer de la liste d'attente"
                              data-testid={`remove-waitlist-${entry.waitlist_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Info box */}
            {!loading && waitlist.length > 0 && (
              <div className="mt-4 p-4 bg-[#FFF8E1] rounded-xl text-sm text-[#645E55]">
                <strong>💡 Fonctionnement :</strong> Quand vous supprimez une inscription, la première personne de la liste d'attente est automatiquement inscrite et notifiée par email.
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Message dialog */}
        <Dialog open={messageDialog} onOpenChange={setMessageDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Envoyer un message</DialogTitle>
              <DialogDescription>
                Ce message sera envoyé à tous les {participants.length} inscrit(s) de cet événement.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="subject">Objet *</Label>
                <Input
                  id="subject"
                  value={messageData.subject}
                  onChange={(e) => setMessageData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Ex: Rappel - Événement demain"
                  className="h-11 border-2 border-[#E5E0D8] rounded-xl"
                  data-testid="message-subject"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  value={messageData.message}
                  onChange={(e) => setMessageData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Votre message..."
                  rows={6}
                  className="border-2 border-[#E5E0D8] rounded-xl resize-none"
                  data-testid="message-content"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMessageDialog(false)}>
                Annuler
              </Button>
              <Button onClick={handleSendMessage} disabled={sending} className="btn-primary">
                {sending ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
